/**
 * PWA Utilities - Support offline et fonctionnalités PWA
 */

export const setupPWA = () => {
  // Vérifier le support
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Workers non supportés par ce navigateur');
    return;
  }

  // Enregistrement du service worker
  navigator.serviceWorker.register('/sw.js').then(
    (registration) => {
      console.log('✨ Service Worker enregistré:', registration);
      
      // Demander la permission des notifications au démarrage
      requestNotificationPermission();
      
      // Vérifier les mises à jour toutes les heures
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);
    },
    (error) => {
      console.error('❌ Erreur enregistrement SW:', error);
    }
  );
};

// Demander la permission pour les notifications
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('⚠️ Notifications non supportées par ce navigateur');
    return false;
  }

  if (Notification.permission === 'granted') {
    console.log('✅ Permission pour les notifications déjà accordée');
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('❌ Permission pour les notifications refusée');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('🔔 Permission des notifications:', permission);
    return permission === 'granted';
  } catch (error) {
    console.error('Erreur lors de la demande de permission:', error);
    return false;
  }
};

// Envoyer une notification simple
export const sendNotification = (title: string, options?: NotificationOptions): void => {
  if (!('Notification' in window)) {
    console.warn('⚠️ Notifications non supportées');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.warn('⚠️ Permission pour les notifications non accordée');
    return;
  }

  try {
    new Notification(title, {
      icon: '/favicon.ico',
      badge: '/manifest.json',
      ...options
    });
    console.log('🔔 Notification envoyée:', title);
  } catch (error) {
    console.error('Erreur lors de l\'envoi de la notification:', error);
  }
};

// Envoyer une notification de message
export const sendMessageNotification = (
  senderName: string,
  messagePreview: string,
  type: 'direct' | 'group' = 'direct',
  data?: Record<string, any>
): void => {
  const title = type === 'group' 
    ? `📢 Nouveau message du groupe`
    : `💬 Nouveau message de ${senderName}`;
  
  const body = messagePreview.substring(0, 100) + (messagePreview.length > 100 ? '...' : '');

  sendNotification(title, {
    body,
    tag: `mboa-message-${type}`,
    requireInteraction: false,
    data: {
      type: 'message',
      messageType: type,
      ...data
    }
  });
};

// Vérifier si on est offline
export const isOffline = () => !navigator.onLine;

// Ajouter des listeners pour le changement de connectivité
export const setupConnectivityListener = (onOnline: () => void, onOffline: () => void) => {
  window.addEventListener('online', () => {
    console.log('🟢 Connecté au réseau');
    onOnline();
  });

  window.addEventListener('offline', () => {
    console.log('🔴 Déconnecté du réseau');
    onOffline();
  });
};

// Installer l'app
export const installApp = async (deferredPrompt: any) => {
  if (!deferredPrompt) {
    console.log('Install prompt not available');
    return;
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  console.log(`App installation: ${outcome}`);
  return outcome === 'accepted';
};
